import React, { ChangeEvent, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import { Checkbox, DatePicker, Radio, Icon, TextArea } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';

import classNames from 'classnames';
import { ScoutingFormListItem } from '../ScoutingFormListItem/ScoutingFormListItem';
import { ScoutingFormTitle } from '../ScoutingFormTitle/ScoutingFormTitle';
import { ScoutingFormNote } from '../ScoutingFormNote/ScoutingFormNote';
import { FilesViewer } from '../FilesViewer/FilesViewer';
import { FilesInput } from '../FilesInput/FilesInput';

import styles from '../scoutingForm.css';
import { ModalWrapper } from '../../../../../components/ModalWrapper/ModalWrapper';
import ConfirmationModal from '../../../../Modal/ConfirmationModal';
import { validateFiles, ValidationResult } from '../../../../../utils/scoutingUtils';

export enum ScoutingDetailsItemType {
  CROP = 'crop',
  GROWTH_STAGE = 'growthStage',
  DATE = 'date',
  NOTE = 'note',
  PHOTO = 'photo',
  TITLE = 'title',
  NONE = 'none',
}

const getItemIcon = (itemType: ScoutingDetailsItemType) => {
  let glyph: string | undefined;
  switch (itemType) {
    case ScoutingDetailsItemType.CROP:
      glyph = Icon.set.CROP_V2;
      break;
    case ScoutingDetailsItemType.GROWTH_STAGE:
      glyph = Icon.set.GROWTH_STAGE;
      break;
    case ScoutingDetailsItemType.DATE:
      glyph = Icon.set.CALENDAR_V2;
      break;
    case ScoutingDetailsItemType.PHOTO:
      glyph = Icon.set.CAMERA_OUTLINED;
      break;
    case ScoutingDetailsItemType.NOTE:
      glyph = Icon.set.NOTEPAD;
      break;
    case ScoutingDetailsItemType.TITLE:
      glyph = Icon.set.NOTEPAD;
      break;
    default:
      break;
  }

  if (!glyph) {
    return undefined;
  }

  return (
    <Icon
      glyph={glyph}
      className={styles.enhancedScoutingFormIcon}
      testId={`enhanced-scouting-form-details-item-icon-${itemType}`} />
  )
};

export type ScoutingFormDetailsProps = {
  children: React.ReactNode;
  coords: (number | undefined)[];
  isSeasonal: boolean;
  isEditing: boolean;
  isLoading: boolean;
  dateSelected: string;
  note: string;
  soilSample: boolean;
  title: string;
  previews: string[];
  setDate: React.Dispatch<React.SetStateAction<string>>;
  setIsSeasonal: React.Dispatch<React.SetStateAction<boolean>>;
  setNote: React.Dispatch<React.SetStateAction<string>>;
  toggleSoilSample: () => void;
  onDelete: () => void;
  onTitleEdit: (title: string) => void;
  onFilesSelection: (newFiles: File[]) => void;
  onFilesEdit: (editIndex: number) => void;
}

export const ScoutingFormDetails = ({
  children,
  coords: [longitude, latitude],
  isSeasonal,
  isEditing,
  isLoading,
  dateSelected,
  title,
  note,
  soilSample,
  previews,
  setDate,
  setIsSeasonal,
  setNote,
  toggleSoilSample,
  onDelete,
  onTitleEdit,
  onFilesSelection,
  onFilesEdit,
}: ScoutingFormDetailsProps) => {
  const inputFile = useRef<HTMLInputElement | null>(null);
  const selectedFiles = useRef<File[]>([]); // To keep track of selected files
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningModalMessage, setWarningModalMessage] = useState('');

  const onAddPhoto = () => {
    inputFile.current?.click();
  };

  const onFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);

    const { validFiles, invalidFileMessage }: ValidationResult = validateFiles(
      files,
      selectedFiles.current.length,
      previews.length,
    );

    if (invalidFileMessage) {
      setWarningModalMessage(invalidFileMessage);
      setShowWarningModal(true);
      return;
    }

    selectedFiles.current = [...selectedFiles.current, ...validFiles];

    onFilesSelection(selectedFiles.current);
  };

  const onFilesInputMounted = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
    inputFile.current = ref.current
  };

  const onNoteChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNote(e.target.value);
  }

  return (
    <div className={`${styles.enhancedScoutingContent} ${styles.enhancedScoutingDetailsContent}`}>
      {showWarningModal && (
        <ModalWrapper>
          <ConfirmationModal
            isWarningModal
            className={styles.warningModal}
            handleClickConfirm={() => setShowWarningModal(false)}
            handleClickGoBack={() => setShowWarningModal(false)}
            handleClickCancel={() => setShowWarningModal(false)}
            title={warningModalMessage}
          />
        </ModalWrapper>
      )}
      <ScoutingFormListItem onClick={isEditingTitle ? undefined : () => setIsEditingTitle(true)}>
        <ScoutingFormTitle isEditingTitle={isEditingTitle} setIsEditingTitle={setIsEditingTitle} title={title} setTitle={onTitleEdit} />
      </ScoutingFormListItem>
      {children}
      <ScoutingFormListItem
        icon={getItemIcon(ScoutingDetailsItemType.NOTE)}
        content={
          isEditingNote
            ? <TextArea
              value={note}
              testId="enhanced-scouting-note-textarea"
              className={styles.enhnancedScoutingNote}
              placeholder={getMessage('enhancedScouting.form.enterNote')}
              onChange={onNoteChange} />
            : note}
        onClick={isEditingNote ? undefined : () => setIsEditingNote(true)}
      >
        <ScoutingFormNote note={note} setNote={setNote} isEditingNote={isEditingNote} setIsEditingNote={setIsEditingNote}>
          {note ? getMessage('scouting.form.note.singular') : getMessage('enhancedScouting.form.addNote')}
        </ScoutingFormNote>
      </ScoutingFormListItem>
      <ScoutingFormListItem
        onClick={onAddPhoto}
        icon={getItemIcon(ScoutingDetailsItemType.PHOTO)}>
        {getMessage('enhancedScouting.form.addPhoto')}
      </ScoutingFormListItem>
      <FilesViewer urls={previews} onEdit={onFilesEdit} />
      <FilesInput onMount={onFilesInputMounted} onChange={onFilesSelected} />
      <ScoutingFormListItem onClick={toggleSoilSample}>
        <Checkbox
          checked={soilSample}
          label={getMessage('enhancedScouting.form.soilSample')}
          onChange={toggleSoilSample}
          className={styles.cursorPointer}
          testId='enhanced-scouting-soil-sample'
        />
      </ScoutingFormListItem>
      <ScoutingFormListItem icon={getItemIcon(ScoutingDetailsItemType.DATE)} clickable>
        <DatePicker
          className={`${styles.enhancedScoutingFormDatePicker} ${styles.cursorPointer}`}
          minDate={moment(dateSelected).subtract(1, 'years').toISOString()}
          maxDate={moment(new Date()).add(1, 'years').toISOString()}
          moment={moment}
          onChangeDate={(date: Moment) => {
            setDate(date.toISOString());
          }}
          selected={moment(dateSelected)}
          testId='enhanced-scouting-form-details-date-picker'
        />
      </ScoutingFormListItem>
      <div className={`${styles.enhancedScoutingDetailsSeasonal} ${styles.modalRow}`}>
        <Radio
          label={getMessage('scouting.form.seasonal')}
          checked={isSeasonal}
          className={styles.cursorPointer}
          testId="enhanced-scouting-form-details-radio-seasonal"
          onChange={() => setIsSeasonal(true)}
        />
        <Radio
          label={getMessage('scouting.form.permanent')}
          checked={!isSeasonal}
          className={styles.cursorPointer}
          testId="enhanced-scouting-form-details-radio-permanent"
          onChange={() => setIsSeasonal(false)}
        />
      </div>
      <div className={`${styles.enhancedScoutingDetailsLocation} ${styles.modalRow}`}>
        {getMessage('enhancedScouting.form.location')} {latitude?.toFixed(4)}, {longitude?.toFixed(4)}
      </div>
      {isEditing ? (
        <div className={styles.modalRow}>
          <button
            type="button"
            onClick={isLoading ? undefined : onDelete}
            className={classNames(styles.enhancedScoutingDeleteNoteButton, isLoading ? styles.enhancedScoutingDeleteNoteButtonDisabled : '')}
            data-test-id="enhanced-scouting-delete-button"
            disabled={isLoading}
          >
            {getMessage('scouting.form.delete.note')}
          </button>
        </div>
      ) : null}
    </div>
  )
};
